/**
 * Multi-Language Support Service (i18n)
 * 
 * Implements internationalization as per PRD FR-009:
 * - 5 languages: English, Nepali, Hindi, Spanish, Portuguese
 * - Dynamic language switching
 * - Agricultural terminology localization
 * - RTL support structure
 * 
 * Reference: PRD Section 5.1, Feature 9
 */

/**
 * Supported languages
 */
export const SUPPORTED_LANGUAGES = {
  en: { code: 'en', name: 'English', nativeName: 'English', rtl: false, flag: '🇺🇸' },
  ne: { code: 'ne', name: 'Nepali', nativeName: 'नेपाली', rtl: false, flag: '🇳🇵' },
  hi: { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', rtl: false, flag: '🇮🇳' },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español', rtl: false, flag: '🇪🇸' },
  pt: { code: 'pt', name: 'Portuguese', nativeName: 'Português', rtl: false, flag: '🇧🇷' }
};

/**
 * Translation dictionary
 */
export const translations = {
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.cropEvaluation': 'Crop Evaluation',
    'nav.sustainability': 'Sustainability Index',
    'nav.biodiversity': 'Biodiversity',
    'nav.satellite': 'Satellite Imagery',
    'nav.pestPrediction': 'Pest Prediction',
    'nav.cropRotation': 'Crop Rotation',
    'nav.climate': 'Climate Adaptation',
    'nav.policy': 'Policy Recommendations',
    'nav.resources': 'Resources',
    'nav.settings': 'Settings',
    'nav.map': 'Map View',
    'nav.conflicts': 'Conflict Resolution',
    'nav.learning': 'Learning Network',
    'nav.sms': 'SMS Notifications',
    
    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.refresh': 'Refresh',
    'common.download': 'Download',
    'common.export': 'Export',
    'common.filter': 'Filter',
    'common.search': 'Search',
    'common.noData': 'No data available',
    'common.submit': 'Submit',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.view': 'View',
    'common.close': 'Close',
    
    // Dashboard
    'dashboard.title': 'Agrotrack Dashboard',
    'dashboard.welcome': 'Welcome to Agrotrack',
    'dashboard.overview': 'System Overview',
    'dashboard.quickStats': 'Quick Statistics',
    'dashboard.recentActivity': 'Recent Activity',
    'dashboard.totalFields': 'Total Fields',
    'dashboard.activeCrops': 'Active Crops',
    'dashboard.alerts': 'Active Alerts',
    
    // Crop Evaluation
    'crop.evaluation': 'Crop Evaluation',
    'crop.yieldPrediction': 'Yield Prediction',
    'crop.confidenceInterval': 'Confidence Interval',
    'crop.scenarios': 'What-If Scenarios',
    'crop.historical': 'Historical Data',
    'crop.fields': 'Fields',
    'crop.area': 'Area',
    'crop.health': 'Health Status',
    'crop.excellent': 'Excellent',
    'crop.good': 'Good',
    'crop.moderate': 'Moderate',
    'crop.poor': 'Poor',
    
    // Biodiversity
    'biodiversity.title': 'Biodiversity Assessment',
    'biodiversity.shannonIndex': 'Shannon Index',
    'biodiversity.simpsonIndex': 'Simpson Index',
    'biodiversity.speciesRichness': 'Species Richness',
    'biodiversity.evenness': 'Evenness',
    'biodiversity.monocultureRisk': 'Monoculture Risk',
    'biodiversity.recommendations': 'Recommendations',
    'biodiversity.score': 'Biodiversity Score',
    'biodiversity.critical': 'Critical',
    'biodiversity.high': 'High',
    'biodiversity.low': 'Low',
    'biodiversity.minimal': 'Minimal',
    
    // Satellite
    'satellite.title': 'Satellite Imagery Analysis',
    'satellite.ndvi': 'NDVI',
    'satellite.cropHealth': 'Crop Health',
    'satellite.timeSeries': 'Time Series',
    'satellite.changeDetection': 'Change Detection',
    'satellite.excellent': 'Excellent',
    'satellite.good': 'Good',
    'satellite.moderate': 'Moderate',
    'satellite.poor': 'Poor',
    'satellite.noVegetation': 'No Vegetation',
    
    // Pest Prediction
    'pest.title': 'Pest & Disease Prediction',
    'pest.riskLevel': 'Risk Level',
    'pest.highRisk': 'High Risk',
    'pest.moderateRisk': 'Moderate Risk',
    'pest.lowRisk': 'Low Risk',
    'pest.forecast': 'Forecast',
    'pest.preventiveActions': 'Preventive Actions',
    'pest.alerts': 'Alerts',
    'pest.affectedCrops': 'Affected Crops',
    
    // Climate
    'climate.title': 'Climate Adaptation',
    'climate.vulnerability': 'Vulnerability Assessment',
    'climate.projections': 'Projections',
    'climate.adaptedCrops': 'Adapted Crops',
    'climate.adaptationPlan': 'Adaptation Plan',
    'climate.temperature': 'Temperature',
    'climate.precipitation': 'Precipitation',
    
    // Policy
    'policy.title': 'Policy Recommendations',
    'policy.priority': 'Priority',
    'policy.critical': 'Critical',
    'policy.high': 'High',
    'policy.medium': 'Medium',
    'policy.low': 'Low',
    'policy.actions': 'Recommended Actions',
    
    // Agricultural Terms
    'agri.crop': 'Crop',
    'agri.soil': 'Soil',
    'agri.irrigation': 'Irrigation',
    'agri.fertilizer': 'Fertilizer',
    'agri.pesticide': 'Pesticide',
    'agri.harvest': 'Harvest',
    'agri.planting': 'Planting',
    'agri.yield': 'Yield',
    'agri.hectare': 'Hectare',
    'agri.acre': 'Acre',
    'agri.tonne': 'Tonne',
    'agri.nitrogen': 'Nitrogen',
    'agri.phosphorus': 'Phosphorus',
    'agri.potassium': 'Potassium',
    'agri.wheat': 'Wheat',
    'agri.rice': 'Rice',
    'agri.corn': 'Corn',
    'agri.soybeans': 'Soybeans',
    
    // Units
    'unit.perHectare': 'per hectare',
    'unit.perAcre': 'per acre',
    'unit.percentage': '%',
    'unit.celsius': '°C',
    'unit.mm': 'mm',
    'unit.days': 'days'
  },
  
  ne: {
    // Navigation - Nepali
    'nav.dashboard': 'ड्यासबोर्ड',
    'nav.cropEvaluation': 'बाली मूल्याङ्कन',
    'nav.sustainability': 'दिगोपन सूचकाङ्क',
    'nav.biodiversity': 'जैविक विविधता',
    'nav.satellite': 'उपग्रह तस्विर',
    'nav.pestPrediction': 'कीरा पूर्वानुमान',
    'nav.cropRotation': 'बाली चक्र',
    'nav.climate': 'जलवायु अनुकूलन',
    'nav.policy': 'नीति सिफारिस',
    'nav.resources': 'स्रोतहरू',
    'nav.settings': 'सेटिङ्हरू',
    'nav.map': 'नक्सा हेर्नुहोस्',
    'nav.conflicts': 'विवाद समाधान',
    'nav.learning': 'सिकाइ नेटवर्क',
    'nav.sms': 'एसएमएस सूचना',
    
    // Common - Nepali
    'common.save': 'सेभ गर्नुहोस्',
    'common.cancel': 'रद्द गर्नुहोस्',
    'common.delete': 'मेटाउनुहोस्',
    'common.edit': 'सम्पादन',
    'common.loading': 'लोड हुँदैछ...',
    'common.error': 'त्रुटि',
    'common.success': 'सफलता',
    'common.refresh': 'रिफ्रेस',
    'common.download': 'डाउनलोड',
    'common.export': 'निर्यात',
    'common.filter': 'फिल्टर',
    'common.search': 'खोज्नुहोस्',
    'common.noData': 'डाटा उपलब्ध छैन',
    'common.submit': 'पेश गर्नुहोस्',
    'common.back': 'पछाडि',
    'common.next': 'अर्को',
    'common.view': 'हेर्नुहोस्',
    'common.close': 'बन्द गर्नुहोस्',
    
    // Dashboard - Nepali
    'dashboard.title': 'एग्रोट्र्याक ड्यासबोर्ड',
    'dashboard.welcome': 'एग्रोट्र्याकमा स्वागत छ',
    'dashboard.overview': 'प्रणाली अवलोकन',
    'dashboard.quickStats': 'द्रुत तथ्याङ्क',
    'dashboard.recentActivity': 'हालको गतिविधि',
    'dashboard.totalFields': 'कुल खेत',
    'dashboard.activeCrops': 'सक्रिय बालीहरू',
    'dashboard.alerts': 'सक्रिय अलर्टहरू',
    
    // Biodiversity - Nepali
    'biodiversity.title': 'जैविक विविधता मूल्याङ्कन',
    'biodiversity.shannonIndex': 'शानन सूचकाङ्क',
    'biodiversity.simpsonIndex': 'सिम्पसन सूचकाङ्क',
    'biodiversity.speciesRichness': 'प्रजाति समृद्धि',
    'biodiversity.monocultureRisk': 'एकल बाली जोखिम',
    'biodiversity.recommendations': 'सिफारिसहरू',
    'biodiversity.score': 'जैविक विविधता स्कोर',
    
    // Agricultural Terms - Nepali
    'agri.crop': 'बाली',
    'agri.soil': 'माटो',
    'agri.irrigation': 'सिंचाई',
    'agri.fertilizer': 'मल',
    'agri.pesticide': 'कीटनाशक',
    'agri.harvest': 'फसल',
    'agri.planting': 'रोपाई',
    'agri.yield': 'उत्पादन',
    'agri.hectare': 'हेक्टर',
    'agri.acre': 'बिघा',
    'agri.wheat': 'गहुँ',
    'agri.rice': 'धान',
    'agri.corn': 'मकै',
    
    // Pest - Nepali
    'pest.title': 'कीरा र रोग पूर्वानुमान',
    'pest.riskLevel': 'जोखिम स्तर',
    'pest.highRisk': 'उच्च जोखिम',
    'pest.moderateRisk': 'मध्यम जोखिम',
    'pest.lowRisk': 'न्यून जोखिम',
    'pest.forecast': 'पूर्वानुमान',
    'pest.preventiveActions': 'रोकथाम कार्यहरू',
    'pest.alerts': 'अलर्टहरू',
    
    // Units - Nepali
    'unit.perHectare': 'प्रति हेक्टर',
    'unit.percentage': '%',
    'unit.celsius': '°C',
    'unit.days': 'दिन'
  },
  
  hi: {
    // Navigation - Hindi
    'nav.dashboard': 'डैशबोर्ड',
    'nav.cropEvaluation': 'फसल मूल्यांकन',
    'nav.sustainability': 'स्थिरता सूचकांक',
    'nav.biodiversity': 'जैव विविधता',
    'nav.satellite': 'उपग्रह इमेजरी',
    'nav.pestPrediction': 'कीट पूर्वानुमान',
    'nav.cropRotation': 'फसल चक्र',
    'nav.climate': 'जलवायु अनुकूलन',
    'nav.policy': 'नीति सिफारिशें',
    'nav.resources': 'संसाधन',
    'nav.settings': 'सेटिंग्स',
    'nav.map': 'मानचित्र दृश्य',
    'nav.conflicts': 'विवाद समाधान',
    'nav.learning': 'शिक्षण नेटवर्क',
    'nav.sms': 'एसएमएस सूचनाएं',
    
    // Common - Hindi
    'common.save': 'सहेजें',
    'common.cancel': 'रद्द करें',
    'common.delete': 'हटाएं',
    'common.edit': 'संपादित करें',
    'common.loading': 'लोड हो रहा है...',
    'common.error': 'त्रुटि',
    'common.success': 'सफलता',
    'common.refresh': 'रिफ्रेश',
    'common.download': 'डाउनलोड',
    'common.export': 'निर्यात',
    'common.filter': 'फ़िल्टर',
    'common.search': 'खोजें',
    'common.noData': 'कोई डेटा उपलब्ध नहीं',
    'common.submit': 'जमा करें',
    'common.back': 'वापस',
    'common.next': 'अगला',
    'common.view': 'देखें',
    'common.close': 'बंद करें',
    
    // Dashboard - Hindi
    'dashboard.title': 'एग्रोट्रैक डैशबोर्ड',
    'dashboard.welcome': 'एग्रोट्रैक में आपका स्वागत है',
    'dashboard.overview': 'सिस्टम अवलोकन',
    'dashboard.quickStats': 'त्वरित आंकड़े',
    'dashboard.recentActivity': 'हाल की गतिविधि',
    'dashboard.totalFields': 'कुल खेत',
    'dashboard.activeCrops': 'सक्रिय फसलें',
    'dashboard.alerts': 'सक्रिय अलर्ट',
    
    // Biodiversity - Hindi
    'biodiversity.title': 'जैव विविधता मूल्यांकन',
    'biodiversity.shannonIndex': 'शैनन सूचकांक',
    'biodiversity.simpsonIndex': 'सिम्पसन सूचकांक',
    'biodiversity.speciesRichness': 'प्रजाति समृद्धि',
    'biodiversity.monocultureRisk': 'एकल फसल जोखिम',
    'biodiversity.recommendations': 'सिफारिशें',
    'biodiversity.score': 'जैव विविधता स्कोर',
    
    // Agricultural Terms - Hindi
    'agri.crop': 'फसल',
    'agri.soil': 'मिट्टी',
    'agri.irrigation': 'सिंचाई',
    'agri.fertilizer': 'उर्वरक',
    'agri.pesticide': 'कीटनाशक',
    'agri.harvest': 'कटाई',
    'agri.planting': 'रोपण',
    'agri.yield': 'उपज',
    'agri.hectare': 'हेक्टेयर',
    'agri.acre': 'एकड़',
    'agri.wheat': 'गेहूं',
    'agri.rice': 'चावल',
    'agri.corn': 'मक्का',
    
    // Pest - Hindi
    'pest.title': 'कीट और रोग पूर्वानुमान',
    'pest.riskLevel': 'जोखिम स्तर',
    'pest.highRisk': 'उच्च जोखिम',
    'pest.moderateRisk': 'मध्यम जोखिम',
    'pest.lowRisk': 'कम जोखिम',
    'pest.forecast': 'पूर्वानुमान',
    'pest.preventiveActions': 'निवारक कार्रवाई',
    'pest.alerts': 'अलर्ट',
    
    // Units - Hindi
    'unit.perHectare': 'प्रति हेक्टेयर',
    'unit.percentage': '%',
    'unit.celsius': '°C',
    'unit.days': 'दिन'
  },
  
  es: {
    // Navigation - Spanish
    'nav.dashboard': 'Panel de Control',
    'nav.cropEvaluation': 'Evaluación de Cultivos',
    'nav.sustainability': 'Índice de Sostenibilidad',
    'nav.biodiversity': 'Biodiversidad',
    'nav.satellite': 'Imágenes Satelitales',
    'nav.pestPrediction': 'Predicción de Plagas',
    'nav.cropRotation': 'Rotación de Cultivos',
    'nav.climate': 'Adaptación Climática',
    'nav.policy': 'Recomendaciones de Política',
    'nav.resources': 'Recursos',
    'nav.settings': 'Configuración',
    'nav.map': 'Vista de Mapa',
    'nav.conflicts': 'Resolución de Conflictos',
    'nav.learning': 'Red de Aprendizaje',
    'nav.sms': 'Notificaciones SMS',
    
    // Common - Spanish
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.refresh': 'Actualizar',
    'common.download': 'Descargar',
    'common.export': 'Exportar',
    'common.filter': 'Filtrar',
    'common.search': 'Buscar',
    'common.noData': 'No hay datos disponibles',
    'common.submit': 'Enviar',
    'common.back': 'Atrás',
    'common.next': 'Siguiente',
    'common.view': 'Ver',
    'common.close': 'Cerrar',
    
    // Dashboard - Spanish
    'dashboard.title': 'Panel de Agrotrack',
    'dashboard.welcome': 'Bienvenido a Agrotrack',
    'dashboard.overview': 'Resumen del Sistema',
    'dashboard.quickStats': 'Estadísticas Rápidas',
    'dashboard.recentActivity': 'Actividad Reciente',
    'dashboard.totalFields': 'Total de Campos',
    'dashboard.activeCrops': 'Cultivos Activos',
    'dashboard.alerts': 'Alertas Activas',
    
    // Biodiversity - Spanish
    'biodiversity.title': 'Evaluación de Biodiversidad',
    'biodiversity.shannonIndex': 'Índice de Shannon',
    'biodiversity.simpsonIndex': 'Índice de Simpson',
    'biodiversity.speciesRichness': 'Riqueza de Especies',
    'biodiversity.monocultureRisk': 'Riesgo de Monocultivo',
    'biodiversity.recommendations': 'Recomendaciones',
    'biodiversity.score': 'Puntuación de Biodiversidad',
    
    // Agricultural Terms - Spanish
    'agri.crop': 'Cultivo',
    'agri.soil': 'Suelo',
    'agri.irrigation': 'Riego',
    'agri.fertilizer': 'Fertilizante',
    'agri.pesticide': 'Pesticida',
    'agri.harvest': 'Cosecha',
    'agri.planting': 'Siembra',
    'agri.yield': 'Rendimiento',
    'agri.hectare': 'Hectárea',
    'agri.acre': 'Acre',
    'agri.wheat': 'Trigo',
    'agri.rice': 'Arroz',
    'agri.corn': 'Maíz',
    
    // Pest - Spanish
    'pest.title': 'Predicción de Plagas y Enfermedades',
    'pest.riskLevel': 'Nivel de Riesgo',
    'pest.highRisk': 'Riesgo Alto',
    'pest.moderateRisk': 'Riesgo Moderado',
    'pest.lowRisk': 'Riesgo Bajo',
    'pest.forecast': 'Pronóstico',
    'pest.preventiveActions': 'Acciones Preventivas',
    'pest.alerts': 'Alertas',
    
    // Units - Spanish
    'unit.perHectare': 'por hectárea',
    'unit.percentage': '%',
    'unit.celsius': '°C',
    'unit.days': 'días'
  },
  
  pt: {
    // Navigation - Portuguese
    'nav.dashboard': 'Painel de Controle',
    'nav.cropEvaluation': 'Avaliação de Culturas',
    'nav.sustainability': 'Índice de Sustentabilidade',
    'nav.biodiversity': 'Biodiversidade',
    'nav.satellite': 'Imagens de Satélite',
    'nav.pestPrediction': 'Previsão de Pragas',
    'nav.cropRotation': 'Rotação de Culturas',
    'nav.climate': 'Adaptação Climática',
    'nav.policy': 'Recomendações de Política',
    'nav.resources': 'Recursos',
    'nav.settings': 'Configurações',
    'nav.map': 'Visualização do Mapa',
    'nav.conflicts': 'Resolução de Conflitos',
    'nav.learning': 'Rede de Aprendizado',
    'nav.sms': 'Notificações SMS',
    
    // Common - Portuguese
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Excluir',
    'common.edit': 'Editar',
    'common.loading': 'Carregando...',
    'common.error': 'Erro',
    'common.success': 'Sucesso',
    'common.refresh': 'Atualizar',
    'common.download': 'Baixar',
    'common.export': 'Exportar',
    'common.filter': 'Filtrar',
    'common.search': 'Pesquisar',
    'common.noData': 'Nenhum dado disponível',
    'common.submit': 'Enviar',
    'common.back': 'Voltar',
    'common.next': 'Próximo',
    'common.view': 'Ver',
    'common.close': 'Fechar',
    
    // Dashboard - Portuguese
    'dashboard.title': 'Painel Agrotrack',
    'dashboard.welcome': 'Bem-vindo ao Agrotrack',
    'dashboard.overview': 'Visão Geral do Sistema',
    'dashboard.quickStats': 'Estatísticas Rápidas',
    'dashboard.recentActivity': 'Atividade Recente',
    'dashboard.totalFields': 'Total de Campos',
    'dashboard.activeCrops': 'Culturas Ativas',
    'dashboard.alerts': 'Alertas Ativos',
    
    // Biodiversity - Portuguese
    'biodiversity.title': 'Avaliação de Biodiversidade',
    'biodiversity.shannonIndex': 'Índice de Shannon',
    'biodiversity.simpsonIndex': 'Índice de Simpson',
    'biodiversity.speciesRichness': 'Riqueza de Espécies',
    'biodiversity.monocultureRisk': 'Risco de Monocultura',
    'biodiversity.recommendations': 'Recomendações',
    'biodiversity.score': 'Pontuação de Biodiversidade',
    
    // Agricultural Terms - Portuguese
    'agri.crop': 'Cultura',
    'agri.soil': 'Solo',
    'agri.irrigation': 'Irrigação',
    'agri.fertilizer': 'Fertilizante',
    'agri.pesticide': 'Pesticida',
    'agri.harvest': 'Colheita',
    'agri.planting': 'Plantio',
    'agri.yield': 'Rendimento',
    'agri.hectare': 'Hectare',
    'agri.acre': 'Acre',
    'agri.wheat': 'Trigo',
    'agri.rice': 'Arroz',
    'agri.corn': 'Milho',
    
    // Pest - Portuguese
    'pest.title': 'Previsão de Pragas e Doenças',
    'pest.riskLevel': 'Nível de Risco',
    'pest.highRisk': 'Risco Alto',
    'pest.moderateRisk': 'Risco Moderado',
    'pest.lowRisk': 'Risco Baixo',
    'pest.forecast': 'Previsão',
    'pest.preventiveActions': 'Ações Preventivas',
    'pest.alerts': 'Alertas',
    
    // Units - Portuguese
    'unit.perHectare': 'por hectare',
    'unit.percentage': '%',
    'unit.celsius': '°C',
    'unit.days': 'dias'
  }
};

// Current language state
let currentLanguage = 'en';

/**
 * Get current language
 */
export const getCurrentLanguage = () => currentLanguage;

/**
 * Set current language
 */
export const setLanguage = (langCode) => {
  if (SUPPORTED_LANGUAGES[langCode]) {
    currentLanguage = langCode;
    // Store in localStorage for persistence
    if (typeof window !== 'undefined') {
      localStorage.setItem('agrotrack-language', langCode);
    }
    // Dispatch event for components to re-render
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('languageChange', { detail: langCode }));
    }
    return true;
  }
  return false;
};

/**
 * Initialize language from storage or browser
 */
export const initializeLanguage = () => {
  if (typeof window !== 'undefined') {
    // Check localStorage first
    const stored = localStorage.getItem('agrotrack-language');
    if (stored && SUPPORTED_LANGUAGES[stored]) {
      currentLanguage = stored;
      return currentLanguage;
    }
    
    // Fall back to browser language
    const browserLang = navigator.language.split('-')[0];
    if (SUPPORTED_LANGUAGES[browserLang]) {
      currentLanguage = browserLang;
      return currentLanguage;
    }
  }
  return currentLanguage;
};

/**
 * Translate a key
 * 
 * @param {string} key - Translation key
 * @param {Object} params - Interpolation parameters
 * @returns {string} Translated text
 */
export const t = (key, params = {}) => {
  const langTranslations = translations[currentLanguage] || translations.en;
  let text = langTranslations[key] || translations.en[key] || key;
  
  // Simple parameter interpolation
  Object.entries(params).forEach(([param, value]) => {
    text = text.replace(new RegExp(`{{${param}}}`, 'g'), value);
  });
  
  return text;
};

/**
 * Get all translations for current language
 */
export const getAllTranslations = () => translations[currentLanguage] || translations.en;

/**
 * Check if current language is RTL
 */
export const isRTL = () => SUPPORTED_LANGUAGES[currentLanguage]?.rtl || false;

/**
 * Get language info
 */
export const getLanguageInfo = (langCode = currentLanguage) => SUPPORTED_LANGUAGES[langCode];

/**
 * Get list of all supported languages
 */
export const getSupportedLanguages = () => Object.values(SUPPORTED_LANGUAGES);

/**
 * Format number for current locale
 */
export const formatNumber = (number, decimals = 2) => {
  const locales = {
    en: 'en-US',
    ne: 'ne-NP',
    hi: 'hi-IN',
    es: 'es-ES',
    pt: 'pt-BR'
  };
  
  return new Intl.NumberFormat(locales[currentLanguage] || 'en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  }).format(number);
};

/**
 * Format date for current locale
 */
export const formatDate = (date, options = {}) => {
  const locales = {
    en: 'en-US',
    ne: 'ne-NP',
    hi: 'hi-IN',
    es: 'es-ES',
    pt: 'pt-BR'
  };
  
  return new Intl.DateTimeFormat(locales[currentLanguage] || 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  }).format(new Date(date));
};

/**
 * Format currency for current locale
 */
export const formatCurrency = (amount, currency = 'USD') => {
  const locales = {
    en: 'en-US',
    ne: 'ne-NP',
    hi: 'hi-IN',
    es: 'es-ES',
    pt: 'pt-BR'
  };
  
  const currencies = {
    en: 'USD',
    ne: 'NPR',
    hi: 'INR',
    es: 'EUR',
    pt: 'BRL'
  };
  
  return new Intl.NumberFormat(locales[currentLanguage] || 'en-US', {
    style: 'currency',
    currency: currencies[currentLanguage] || currency
  }).format(amount);
};

const i18nService = {
  t,
  setLanguage,
  getCurrentLanguage,
  initializeLanguage,
  getAllTranslations,
  isRTL,
  getLanguageInfo,
  getSupportedLanguages,
  formatNumber,
  formatDate,
  formatCurrency,
  SUPPORTED_LANGUAGES,
  translations
};

export default i18nService;
